// Composition root: the one place that wires the dashboard's concrete
// widgets into the registry. Add a new one with an import + list entry.
import { dashboardWidgetRegistry } from '@/core/widgets/export';
import {
  BulletListWidget,
  SearchWidget,
  MainwireScrollerWidget,
  PinnedArticleWidgetFactory,
  PinnedSeriesWidgetFactory,
  subject,
} from '@/modules/articles/export';
import {
  DataCalendarWidget,
  SingleCalendarWidgetFactory,
  DataReleaseHistoryWidget,
  hscWidget,
} from '@/modules/calendar/export';
import { ClockWidget } from '@/modules/world-clock/export';
import { PodcastWidgetFactory } from '@/modules/podcasts/export';
import { AssistantWidget } from '@/core/assistant/export';
import {
  SocialMediaNewsWidget,
  MarketSignalScrollerWidget,
  MarketSignalSingleScrollerWidget,
  TrumpTrackerWidget,
} from '@/modules/market-signals/export';
import { TradeRuleBuilderWidget } from '@/modules/trade-rule-builder/export';
import { isAlphaFlashHost } from '@/shared/theme/export';
import { PipelineAlertScrollerWidget, pipelineMapWidget } from '@/modules/pipelines/export';
import { withMobileMeta } from '@/app/dashboard-widgets-mobile-meta';

[
  withMobileMeta(
    new BulletListWidget(
      'all-bullets',
      [
        'fx-bullets',
        'fi-bullets',
        'em-bullets',
        'eu-credit-bullets',
        'eu-fi-bullets',
        'us-credit-bullets',
        'us-hy-credit-bullets',
        'em-credit-bullets',
        'oil-bullets',
        'power-bot',
        'gas-bullets',
        'us-oil',
        'us-gas-bullets',
        'commodities-macro-bullets',
      ],
      {
        title: 'All Bullets',
        titleSubstitutions: [{ codes: ['us-oil', 'us-gas-bullets'], name: 'US O&G' }],
      },
      'All',
    ),
    { mobileCategory: 'Bullets' },
  ),
  withMobileMeta(
    new BulletListWidget('em-bullets', ['em-bullets'], { title: 'EM Bullets' }, 'FI'),
    {
      mobileCategory: 'Bullets',
    },
  ),
  withMobileMeta(
    new BulletListWidget('dm-bullets', ['fx-bullets', 'fi-bullets'], { title: 'DM Bullets' }, 'FX'),
    { mobileCategory: 'Bullets' },
  ),
  withMobileMeta(
    new BulletListWidget(
      'gm-bullets',
      ['dm-gm-bullets', 'em-gm-bullets'],
      { title: 'Global Macro Bullets' },
      'GM',
    ),
    { mobileCategory: 'Bullets' },
  ),
  withMobileMeta(
    new BulletListWidget(
      'credit-bullets',
      [
        'eu-credit-bullets',
        'eu-fi-bullets',
        'us-credit-bullets',
        'us-hy-credit-bullets',
        'em-credit-bullets',
      ],
      { title: 'Credit Bullets' },
      'CR',
      [
        {
          title: 'Regions',
          items: [
            subject('CEEMEA', 'd-ceemea'),
            subject('Latam', '480257886'),
            subject('Asia', 'd-asia'),
          ],
          visible: ['em-credit-bullets'],
        },
        {
          title: 'Credit Sectors',
          items: [
            subject('Automotive', 'd-glob-automotive'),
            subject('Basic Industries', 'd-glob-basic-industries'),
            subject('Capital Goods', 'd-glob-capital-goods'),
            subject('Communications', 'd-glob-communications'),
            subject('Consumer Cyclicals', 'd-glob-consumer-cyclicals'),
            subject('Consumer Discretionary', 'd-glob-consumer-discretionary'),
            subject('Consumer Staples', 'd-glob-consumer-staples'),
            subject('Credit Energy', 'd-glob-credit-energy'),
            subject('Financials', 'd-glob-financials'),
            subject('Healthcare', 'd-glob-healthcare'),
            subject('Real Estate', 'd-glob-real-estate'),
            subject('Technology', 'd-glob-technology'),
            subject('Transportation', 'd-glob-transportation'),
            subject('Utilities', 'd-glob-utilities'),
          ],
          visible: ['us-credit-bullets', 'us-hy-credit-bullets', 'eu-credit-bullets'],
        },
      ],
    ),
    { mobileCategory: 'Bullets' },
  ),
  withMobileMeta(
    new BulletListWidget(
      'energy-bullets',
      [
        'oil-bullets',
        'power-bot',
        'gas-bullets',
        'us-oil',
        'us-gas-bullets',
        'commodities-macro-bullets',
      ],
      {
        title: 'Energy Bullets',
        titleSubstitutions: [{ codes: ['us-oil', 'us-gas-bullets'], name: 'US O&G' }],
      },
      'Oil',
      [
        {
          title: 'EU Power Regions',
          items: [
            subject('UK', '480257818'),
            subject('CWE', 'd-cwe'),
            subject('Iberia', 'd-iberia'),
            subject('Nordics', 'd-Nordics'),
            subject('CEE', 'd-cee'),
            subject('SEE', 'd-see'),
          ],
          visible: ['power-bot'],
          requiredInclude: {
            title: 'Emissions',
            item: subject('Emissions', 'd-emissions'),
          },
        },
      ],
    ),
    { mobileCategory: 'Bullets' },
  ),
  new MainwireScrollerWidget(
    'mainwire',
    ['mainwire', 'em-mainwire'],
    { title: 'Policy Mainwire' },
    'Mainwire',
  ),

  withMobileMeta(new DataCalendarWidget(), { deepDiveId: hscWidget.id }),
  new SocialMediaNewsWidget(),
  new TradeRuleBuilderWidget(),
  new SearchWidget(),
  withMobileMeta(new DataReleaseHistoryWidget(), { deepDiveId: hscWidget.id }),

  //new HotListWidget(),
  withMobileMeta(new PipelineAlertScrollerWidget(), { deepDiveId: pipelineMapWidget.id }),
  //new ChatWidget(),
  new ClockWidget(),
  new AssistantWidget(),
  new TrumpTrackerWidget(),
  new MarketSignalScrollerWidget(),
  new MarketSignalSingleScrollerWidget('all-trump', 'Truth Social', [
    'read:select:instantSocial',
    'read:select:instantSocialBasic',
  ]),
].forEach(widget => dashboardWidgetRegistry.register(widget));

if (!isAlphaFlashHost()) {
  [
    new SingleCalendarWidgetFactory(),
    new PinnedArticleWidgetFactory(),
    new PinnedSeriesWidgetFactory(),
    new PodcastWidgetFactory(),
  ].forEach(factory => dashboardWidgetRegistry.registerFactory(factory));
}
